import {
  ApplicationInsights,
  DistributedTracingModes,
} from "@microsoft/applicationinsights-web"

let clientTelemetryInstance
const WEB_VITAL_PREFIX = "Web Vitals - "

const isEnabled = value => String(value || "").toLowerCase() === "true"

const getSamplingPercentage = value => {
  const parsedValue = Number(value)
  return Number.isFinite(parsedValue) ? parsedValue : 100
}

const getEnvironmentName = () =>
  process.env.NEXT_PUBLIC_APP_ENV ||
  process.env.NEXT_PUBLIC_GLOBALCONFENV ||
  process.env.NODE_ENV ||
  "development"

const getRoleName = () =>
  process.env.NEXT_PUBLIC_APPINSIGHTS_ROLE_NAME ||
  process.env.APPINSIGHTS_ROLE_NAME ||
  "gbh-annsacks-nextjs-web"

const getConnectionString = () =>
  process.env.NEXT_PUBLIC_APPLICATIONINSIGHTS_CONNECTION_STRING

const shouldInitialize = () =>
  typeof window !== "undefined" &&
  !!getConnectionString() &&
  isEnabled(process.env.NEXT_PUBLIC_ENABLE_APPINSIGHTS_LOGGING)

const enrichTelemetry = envelope => {
  envelope.tags = envelope.tags || []
  envelope.tags["ai.cloud.role"] = getRoleName()
  envelope.data = envelope.data || {}
  envelope.data.environment = getEnvironmentName()
  return true
}

export const initializeClientTelemetry = () => {
  if (clientTelemetryInstance || !shouldInitialize()) {
    return clientTelemetryInstance || null
  }

  clientTelemetryInstance = new ApplicationInsights({
    config: {
      connectionString: getConnectionString(),
      enableAutoRouteTracking: true,
      enableCorsCorrelation: true,
      enableAjaxPerfTracking: true,
      enableUnhandledPromiseRejectionTracking: true,
      autoTrackPageVisitTime: true,
      disableFetchTracking: false,
      disableAjaxTracking: false,
      disableExceptionTracking: false,
      distributedTracingMode: DistributedTracingModes.AI_AND_W3C,
      samplingPercentage: getSamplingPercentage(
        process.env.NEXT_PUBLIC_APPINSIGHTS_SAMPLING_PERCENTAGE
      ),
      excludeRequestFromAutoTrackingPatterns: [
        /\/api\/health$/,
        /dc\.services\.visualstudio\.com/i,
        /browser\.events\.data\.microsoft\.com/i,
      ],
    },
  })

  clientTelemetryInstance.loadAppInsights()
  clientTelemetryInstance.addTelemetryInitializer(enrichTelemetry)
  clientTelemetryInstance.trackPageView()

  return clientTelemetryInstance
}

const getClientTelemetry = () =>
  clientTelemetryInstance || initializeClientTelemetry()

export const trackClientException = (error, properties = {}) => {
  const clientTelemetry = getClientTelemetry()
  if (!clientTelemetry || !error) return

  clientTelemetry.trackException({
    error,
  }, properties)
}

export const trackClientMetric = (name, average, properties = {}) => {
  const clientTelemetry = getClientTelemetry()
  if (!clientTelemetry || !name || average === undefined) return

  clientTelemetry.trackMetric({
    name,
    average,
  }, properties)
}

const getNavigationTypeForTelemetry = metric => {
  if (metric?.navigationType && metric.navigationType !== "undefined") {
    return metric.navigationType
  }

  if (typeof window === "undefined" || !window.performance) {
    return "unknown"
  }

  const navigationEntry = window.performance.getEntriesByType?.("navigation")?.[0]
  return navigationEntry?.type || "unknown"
}

const getPlatformForTelemetry = () => {
  if (typeof window === "undefined") {
    return "unknown"
  }

  return (
    window.navigator?.userAgentData?.platform ||
    window.navigator?.platform ||
    "unknown"
  )
}

const getRatingForTelemetry = metric => {
  if (metric?.label !== "web-vital") {
    return "n/a"
  }

  if (metric?.rating) {
    return metric.rating
  }

  const value = Number(metric?.value)

  switch (metric?.name) {
    case "CLS":
      if (value <= 0.1) return "good"
      if (value <= 0.25) return "needs-improvement"
      return "poor"
    case "INP":
      if (value <= 200) return "good"
      if (value <= 500) return "needs-improvement"
      return "poor"
    case "LCP":
      if (value <= 2500) return "good"
      if (value <= 4000) return "needs-improvement"
      return "poor"
    case "FCP":
      if (value <= 1800) return "good"
      if (value <= 3000) return "needs-improvement"
      return "poor"
    case "TTFB":
      if (value <= 800) return "good"
      if (value <= 1800) return "needs-improvement"
      return "poor"
    default:
      return "unknown"
  }
}

export const trackWebVitalMetric = (metric, { isMobile = false } = {}) => {
  if (!metric?.name || metric?.value === undefined) return null

  const path = typeof window !== "undefined" ? window.location?.pathname : ""
  const url = typeof window !== "undefined" ? window.location?.href : ""
  const metricName =
    metric?.label === "web-vital"
      ? `${WEB_VITAL_PREFIX}${metric.name}`
      : metric.name
  const telemetryProperties = {
    id: metric.id,
    name: metric.name,
    path,
    startTime: String(metric.startTime ?? ""),
    value: String(metric.value ?? ""),
    label: metric.label,
    rating: getRatingForTelemetry(metric),
    url,
    navigationType: getNavigationTypeForTelemetry(metric),
    deviceType: JSON.stringify({
      isMobile,
      platform: getPlatformForTelemetry(),
    }),
  }

  trackClientMetric(metricName, metric.value, telemetryProperties)

  const telemetryPayload = {
    name: metricName,
    value: metric.value,
    customDimensions: telemetryProperties,
  }

  if (typeof window !== "undefined") {
    window.__lastWebVital = telemetryPayload
    window.__webVitalsHistory = window.__webVitalsHistory || []
    window.__webVitalsHistory.push(telemetryPayload)
  }

  return telemetryPayload
}
